import React, { useContext } from 'react';

import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { bookingInquiryProfitStrings } from '@data/bookingInquiryProfit';
import { moneyStrings } from '@data/money';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import * as R from 'remeda';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const strings = createStrings({
  ja: {},
});

const useStyles = makeMuiStyles((theme, common) => ({
  bookingInquiryProfitPanelContainer: {
    ...common.flexInline,
    columnGap: 40,
  },
  bookingInquiryProfitSection: {
    ...common.middleText,
    ...common.textGray500,
    ...common.backgroundPrimaryAlpha5,
    padding: 20,
    '& *[role="title"]': {
      ...common.normalText,
      ...common.fontWeight600,
      ...common.textPrimaryDark,
      marginBottom: 8,
      '$profitPart&': {
        opacity: 0.5,
      },
    },
    '& *[role="total"]': {
      ...common.normalText,
      ...common.fontWeight600,
      ...common.textPrimaryDark,
      '$profitPart&': {
        opacity: 0.5,
      },
    },
    '& *[role="data"]': {
      ...common.textAlignRight,
    },
    '&$profitPart': {
      backgroundColor: 'transparent',
    },
  },
  bookingInquiryProfitTable: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: 40,
    paddingBottom: 10,
  },
  bookingInquiryProfitSplit: {
    ...common.colSpanFull,
    margin: '8px 0',
    borderBottom: `1px dashed ${theme.palette.grey[500]}`,
  },
  profitPart: {},
}));

export const BookingInquiryProfitPanel: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { preAlert } = useContext(BookingDetailsContext);

  const { profit } = preAlert;

  const formatPrice = (price: number | undefined) => {
    return price != null ? moneyStrings.formatYen(price) : '-';
  };

  const maybeSubtract = (a: number | undefined, b: number | undefined) => {
    if (a == null || b == null) {
      return undefined;
    } else {
      return a - b;
    }
  };

  const maybeSum = (arr: (number | undefined)[]) => {
    if (arr.every((a) => a === undefined)) return undefined;

    return R.pipe(
      arr,
      R.sumBy((x) => x!),
    );
  };

  return (
    <div className={classes.bookingInquiryProfitPanelContainer}>
      <div style={{ flex: 1 }} />
      {/* Invoice */}
      <div className={clsx(classes.bookingInquiryProfitSection, classes.profitPart)}>
        <div role="title">{bookingInquiryProfitStrings.invoice}</div>

        <div className={classes.bookingInquiryProfitTable}>
          <div>{bookingInquiryProfitStrings.invoiceTaxExcludedAmount}</div>
          <div>{formatPrice(profit?.invoice?.tax_excluded_total_price)}</div>

          <div>{bookingInquiryProfitStrings.invoiceTaxAmount}</div>
          <div>{formatPrice(profit?.invoice?.excise_tax)}</div>

          <div>{bookingInquiryProfitStrings.invoiceTatekaeAmount}</div>
          <div>{formatPrice(profit?.invoice?.tatekae_total_price)}</div>

          <div className={classes.bookingInquiryProfitSplit} />

          <div>
            <span role="total">{bookingInquiryProfitStrings.total}</span>
          </div>
          <div role="data">
            <span role="total">
              {formatPrice(
                maybeSum([
                  profit?.invoice?.tax_excluded_total_price,
                  profit?.invoice?.excise_tax,
                  profit?.invoice?.tatekae_total_price,
                ]),
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Payment */}
      <div className={clsx(classes.bookingInquiryProfitSection, classes.profitPart)}>
        <div role="title">{bookingInquiryProfitStrings.payment}</div>

        <div className={classes.bookingInquiryProfitTable}>
          <div>{bookingInquiryProfitStrings.paymentTaxExcludedAmount}</div>
          <div>{formatPrice(profit?.payment?.tax_excluded_total_price)}</div>

          <div>{bookingInquiryProfitStrings.paymentTaxAmount}</div>
          <div>{formatPrice(profit?.payment?.excise_tax)}</div>

          <div>{bookingInquiryProfitStrings.paymentTatekaeAmount}</div>
          <div>{formatPrice(profit?.payment?.tatekae_total_price)}</div>

          <div className={classes.bookingInquiryProfitSplit} />

          <div>
            <span role="total">{bookingInquiryProfitStrings.total}</span>
          </div>
          <div role="data">
            <span role="total">
              {formatPrice(
                maybeSum([
                  profit?.payment?.tax_excluded_total_price,
                  profit?.payment?.excise_tax,
                  profit?.payment?.tatekae_total_price,
                ]),
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Profit */}
      <div className={classes.bookingInquiryProfitSection}>
        <div role="title">{bookingInquiryProfitStrings.profit}</div>

        <div className={classes.bookingInquiryProfitTable}>
          <div>
            {bookingInquiryProfitStrings.invoiceTaxExcludedAmount}
            {' - '}
            {bookingInquiryProfitStrings.paymentTaxExcludedAmount}
          </div>
          <div role="data">
            {formatPrice(
              maybeSubtract(
                profit?.invoice?.tax_excluded_total_price,
                profit?.payment?.tax_excluded_total_price,
              ),
            )}
          </div>

          <div className={classes.bookingInquiryProfitSplit} />

          <div>
            <span role="total">{bookingInquiryProfitStrings.profit}</span>
          </div>
          <div role="data">
            <span role="total">
              {formatPrice(
                maybeSubtract(
                  profit?.invoice?.tax_excluded_total_price,
                  profit?.payment?.tax_excluded_total_price,
                ),
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
